import React, { createContext, useState, useEffect, useContext } from 'react';
import { clearStoredAuth, getStoredAccessToken } from '../api/axios';
import { loginAdmin, resendAdminOtp, verifyAdminOtp } from '../api/services';
import {
  ACCESS_TOKEN_KEY,
  LEGACY_ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ADMIN_USER_KEY,
} from '../utils/admin';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem(ADMIN_USER_KEY);
    const storedToken = getStoredAccessToken();

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
        localStorage.setItem(ACCESS_TOKEN_KEY, storedToken);
      } catch (e) {
        console.error('Failed to parse user data', e);
        logout();
      }
    }

    const handleForcedLogout = () => {
      setUser(null);
    };

    window.addEventListener('admin-auth-logout', handleForcedLogout);
    setLoading(false);

    return () => {
      window.removeEventListener('admin-auth-logout', handleForcedLogout);
    };
  }, []);

  const persistSession = (admin, accessToken, refreshToken) => {
    setUser(admin);
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(LEGACY_ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(admin));

    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  };

  const requestOtp = async (phoneNumber) => {
    try {
      const data = await loginAdmin(phoneNumber);
      if (data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: 'Failed to send OTP' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'An error occurred requesting OTP' 
      };
    }
  };

  const verifyOtp = async (phoneNumber, otp) => {
    try {
      const data = await verifyAdminOtp(phoneNumber, otp);
      if (data.success) {
        const { admin, accessToken, refreshToken } = data;
        persistSession(admin, accessToken, refreshToken);
        return { success: true, admin };
      } else {
        return { success: false, error: 'Verification failed' };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Invalid OTP' 
      };
    }
  };

  const resendOtp = async (phoneNumber) => {
    try {
      const data = await resendAdminOtp(phoneNumber);
      return { success: true, message: data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to resend OTP' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    clearStoredAuth();
  };

  return (
    <AuthContext.Provider value={{ user, requestOtp, verifyOtp, resendOtp, logout, loading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

